import React, { useEffect, useMemo, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import photosLogo from "../../assets/images/post/photos.svg";
import productLogo from "../../assets/images/post/Product.svg";
import artWorkLogo from "../../assets/images/post/Artwork.svg";
import serviceLogo from "../../assets/images/post/service.svg";
import videoLogo from "../../assets/images/post/videos.svg";
import AlertPopUp from "../../common/AlertPopUp";
import { getCountryList } from "../../appStore/actions/masterData/masterDataAction";
import {
  handleSavePreviewEditPostData,
  handleSetserviceTypePost,
} from "../../appStore/actions/AppState/action";
import {
  AccountLevelArkchatPermission,
  accountType,//import
} from "../../utils/constant";
import { StyledSideBarInviteButton } from "../../common/StyledComponents/SideBarComponents";
import useAppSelectors from "../../customHooks/useAppSelectors";
import { colorSchemes, fontSizeSchemes } from "../../assets/commonFunction";
import SwitchAccount from "../Switch/SwitchAccount";
import NewOrganisationProfile from "../Profille/NewOrganisationProfile";
import tickmarkimg from "../../assets/images/post/tickMarkIcon.svg";

const organisationServiceTypeData = [
  { image: productLogo, title: "Products", serviceType: 2 },
  { image: artWorkLogo, title: "Art", serviceType: 3 },
  { image: serviceLogo, title: "Service", serviceType: 1 },
];

const individualServiceTypeData = [
  { image: artWorkLogo, title: "Art", serviceType: 3 },
  { image: serviceLogo, title: "Service", serviceType: 1 },
];

const EXPLORE_OPTIONS = [
  { image: photosLogo, title: "Photos", serviceType: 4 },
  { image: videoLogo, title: "Videos", serviceType: 5 },
];

const cardSx = {
  width: { xs: "120px", sm: "120px" },
  height: "120px",
  border: "1px solid #DEDEDE",
  borderRadius: "24px",
  boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.12)",
  backgroundColor: "#FFFFFF",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 1.25,
  px: 1.5,
  py: 1.5,
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.14)",
  },
};

const bulletTextSx = { fontSize: "0.9rem", lineHeight: 1.8 };
const sectionHeadingSx = {
  width: "100%",
  maxWidth: "286px",
  mx: "auto",
  mb: 5,
  fontFamily: "Poppins, sans-serif",
  fontSize: fontSizeSchemes.font17,
  fontWeight: 500,
  lineHeight: "100%",
  letterSpacing: "0px",
  textAlign: "center",
  color: "#000000",
};

const PostLandingpage = () => {
  const { t } = useTranslation();
  const appContent = t("AppContent", { returnObjects: true });
  const [isAccessDenied, setAccessDenied] = useState(false);
  const [isSwitchAccountDrawerOpen, setSwitchAccountDrawerOpen] =
    useState(false);
  const [isCreateNewOrgAccountDrawerOpen, setCreateNewOrgAccountDrawerOpen] =
    useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.profile?.profileData);
  const { currentUserData } = useAppSelectors();

  const activeSubscriber = userData?.data?.activeSubscriber;
  const activePermission = userData?.data?.activePermission;
  const selectedNetworkType = activeSubscriber?.accountServiceType;
  const isOrganisation =
    activeSubscriber?.subscriberPriority === accountType?.Organisation;
  const isIndividual =
    activeSubscriber?.subscriberPriority === accountType?.Individual;

  const availableNetworks = useMemo(() => {
    if (isOrganisation) return organisationServiceTypeData;
    if (isIndividual) return individualServiceTypeData;
    return [];
  }, [isIndividual, isOrganisation]);

  const visibleNetworks = useMemo(() => {
    if (!selectedNetworkType) return availableNetworks;

    return availableNetworks.filter(
      (item) => item.serviceType === selectedNetworkType
    );
  }, [availableNetworks, selectedNetworkType]);

  const handleClickCard = (type) => {
    localStorage.clear();
    dispatch(handleSavePreviewEditPostData({}));
    localStorage.setItem("tabValue", 0);

    if (
      isOrganisation &&
      activePermission !== AccountLevelArkchatPermission?.USER
    ) {
      dispatch(handleSetserviceTypePost(type));
      navigate("/post/post-create");
      return;
    }

    if (
      isIndividual &&
      activePermission !== AccountLevelArkchatPermission?.USER
    ) {
      dispatch(handleSetserviceTypePost(type));
      navigate("/post/post-create");
      return;
    }

    setAccessDenied(true);
  };

  useEffect(() => {
    dispatch(getCountryList());
  }, [dispatch]);

  const renderCard = (item) => (
    <Box
      key={`${item.title}-${item.serviceType}`}
      sx={cardSx}
      onClick={() => handleClickCard(item.serviceType)}
    >
      <Box
        component="img"
        src={item.image}
        alt={item.title}
        sx={{
          width: "47.42px",
          height: "40.61px",
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          width: "100%",
          maxWidth: "71px",
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "100%",
          letterSpacing: "0px",
          minHeight: "24px",
          color: "#000000",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.title}
      </Typography>
    </Box>
  );

  return (
    <>
      <Drawer
        sx={{ zIndex: 1202 }}
        open={isSwitchAccountDrawerOpen}
        anchor={"right"}
      >
        <SwitchAccount
          setSwitchAccountDrawerOpen={setSwitchAccountDrawerOpen}
          setCreateNewOrgAccountDrawerOpen={setCreateNewOrgAccountDrawerOpen}
        />
      </Drawer>

      <Drawer
        sx={{ zIndex: 1202 }}
        open={isCreateNewOrgAccountDrawerOpen}
        anchor={"right"}
      >
        <NewOrganisationProfile
          setCreateNewOrgAccountDrawerOpen={setCreateNewOrgAccountDrawerOpen}
          setSwitchAccountDrawerOpen={setSwitchAccountDrawerOpen}
        />
      </Drawer>

      <Box
        sx={{
          minHeight: "calc(100dvh - 72px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <AlertPopUp
          title={"Access Denied ."}
          bodyContent={appContent?.KEY169}
          open={isAccessDenied}
          closeModal={setAccessDenied}
        />

        <Box
          bgcolor={"#F3F3F3"}
          minHeight={"2.5rem"}
          position={"relative"}
          mb={{ xs: "2rem", md: "3rem" }}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
          px={{ xs: 1.5, md: 4 }}
          py={0.75}
          gap={1}
          sx={{
            "@media (max-height: 820px)": {
              mb: "2rem",
            },
          }}
        >
          <Box onClick={() => navigate("/post/manage-mypost")}>
            <StyledSideBarInviteButton
              sx={{ fontWeight: 500, fontSize: fontSizeSchemes.font17 }}
            >
              Edit Post
            </StyledSideBarInviteButton>
          </Box>

          <Box onClick={() => navigate("/post/manage-mypost")}>
            <StyledSideBarInviteButton sx={{ fontWeight: 400 }}>
              <Typography
                color={"#111111"}
                fontWeight={600}
                fontSize={fontSizeSchemes.font16}
                title={`Posting in : ${currentUserData?.activeSubscriber?.subscriberFirmName}`}
                sx={{ textAlign: "center" }}
              >
                Posting in : {currentUserData?.activeSubscriber?.subscriberFirmName}
              </Typography>
            </StyledSideBarInviteButton>
          </Box>

          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setSwitchAccountDrawerOpen(true);
            }}
          >
            <StyledSideBarInviteButton
              sx={{
                fontWeight: 600,
                fontSize: fontSizeSchemes.font14,
                color: colorSchemes.switchTxtLink,
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": {
                  color: "#1a5fb4",
                  textDecoration: "underline",
                },
              }}
            >
              Switch
            </StyledSideBarInviteButton>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: 2, md: 6 },
            pb: { xs: 2, md: 2 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1px 1fr" },
              alignItems: "stretch",
              minHeight: { md: "380px" },
              flex: 1,
              "@media (max-height: 820px)": {
                minHeight: "340px",
              },
            }}
          >
            <Box
              sx={{
                pr: { xs: 0, md: 6 },
                pb: { xs: 4, md: 2 },
                pt: { xs: 0, md: 2 },
                textAlign: "center",
              }}
            >
              <Typography sx={sectionHeadingSx}>
                List on the Arkchat Network
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 3,
                  mb: { xs: 4, md: 3 },
                }}
              >
                {visibleNetworks.map(renderCard)}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: "420px",
                  mx: "auto",
                  textAlign: "left",
                }}
              >
                <Typography sx={bulletTextSx}>
                  <img src={tickmarkimg} alt="image" /> List your products, services or art.
                </Typography>
                <Typography sx={bulletTextSx}>
                  <img src={tickmarkimg} alt="image" /> Businesses can discover you and collaborate instantly.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "block" },
                backgroundColor: "#D8D8D8",
                width: "1px",
                alignSelf: "stretch",
              }}
            />

            <Box
              sx={{
                pl: { xs: 0, md: 6 },
                pb: { xs: 0, md: 2 },
                pt: { xs: 1, md: 2 },
                textAlign: "center",
              }}
            >
              <Typography sx={sectionHeadingSx}>
                Share on Explore
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 3,
                  mb: { xs: 4, md: 3 },
                }}
              >
                {EXPLORE_OPTIONS.map(renderCard)}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: "360px",
                  mx: "auto",
                  textAlign: "left",
                }}
              >
                <Typography sx={bulletTextSx}>
                  <img src={tickmarkimg} alt="image" /> Share photos and videos.
                </Typography>
                <Typography sx={bulletTextSx}>
                  <img src={tickmarkimg} alt="image" /> Build your brand and let businesses connect with you.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              mt: { xs: 2, md: 3 },
              textAlign: "center",
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "100%",
              letterSpacing: "0px",
              color: "#000000",
            }}
          >
            Free to post. No commissions. Ever.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PostLandingpage;
